<script lang="ts">
/**
 * Summary card for the container you've drilled into.
 *
 * Shows the container's thumbnail, name, child counts, and inventory
 * progress. Rendered only when the user has drilled into a container
 * (not at root level).
 *
 * @example
 * ```vue
 * <ContainerScopeCard :container="scope_container" @edit="openEdit" />
 * ```
 */
export interface ContainerScopeCardProps {
  /** Scope container data from the backend. */
  container: ScopeContainer
}
</script>

<script setup lang="ts">
import { SbProgressBar } from "@unio/saltbox-ui"
import { computed } from "vue"

import { ICON } from "@/lib/icons"
import type { ScopeContainer } from "@/types/models"

const props = defineProps<ContainerScopeCardProps>()

const emit = defineEmits<{
  edit: []
}>()

const subtitle = computed(() => {
  const parts: string[] = []
  if (props.container.sub_container_count) {
    parts.push(`${props.container.sub_container_count} sub-containers`)
  }
  if (props.container.item_count) {
    parts.push(`${props.container.item_count} loose items`)
  }
  if (props.container.total_item_count) {
    parts.push(`${props.container.total_item_count} total`)
  }
  return parts.join(" · ")
})
</script>

<template>
  <div class="border-default bg-muted flex items-center gap-4 rounded-lg border p-4">
    <UAvatar
      :src="container.thumb_url ?? undefined"
      :alt="container.name"
      size="xl"
      :ui="{ image: 'object-cover' }"
    />
    <div class="min-w-0 flex-1">
      <div class="text-highlighted text-base font-semibold">{{ container.name }}</div>
      <div class="text-muted mt-1 text-xs">{{ subtitle }}</div>
    </div>
    <SbProgressBar
      :current="container.inventoried_count"
      :total="container.total_item_count"
      size="md"
    />
    <UButton
      :icon="ICON.edit"
      color="neutral"
      variant="ghost"
      size="sm"
      square
      @click="emit('edit')"
    />
  </div>
</template>
